/**
 * Where every route the console used to have now lives.
 *
 * As data because two things consume it and they must not be able to disagree: `App.tsx` turns each
 * row into a `<Navigate replace/>`, and `layout.spec.ts` drives the same table to prove every one
 * actually arrives. A redirect that exists in the router and not in the gate is a redirect nobody
 * notices breaking.
 *
 * Kept indefinitely rather than for a deprecation window. They cost one route each, bookmarks exist,
 * the end-to-end suite navigates by URL throughout, and the screenshots in `server/screenshots` are
 * named after the old paths. There is no date at which deleting them helps anybody.
 *
 * Two of these are re-parentings rather than renames, and they are the ones worth reading. `/events`
 * becomes a *view of* Cases rather than a screen of its own, because a detection feed is an alert
 * with the fold opened up. `/explore` becomes the Query tab beside it: the design folds it into the
 * always-present query bar, and the bar is the right entry point, but a query surface is more than an
 * input - Explore validates as you type, saves a query and exports a result. The tab is where those
 * live until the bar can do them (D-325).
 */
export interface LegacyRoute {
  from: string
  to: string
}

export const LEGACY_ROUTES: readonly LegacyRoute[] = [
  { from: '/dashboard', to: '/board' },
  { from: '/alerts', to: '/cases' },
  // The id carries over: a case *is* the alert, renamed. Minting new identifiers here would break
  // every link anybody has ever pasted into a ticket.
  { from: '/alerts/:alertId', to: '/cases/:alertId' },
  { from: '/events', to: '/cases?feed=raw' },
  { from: '/events/:eventRecordId', to: '/events/:eventRecordId' },
  { from: '/explore', to: '/cases?tab=query' },
  { from: '/dictionaries', to: '/policies?tab=dictionaries' },
  { from: '/assistant', to: '/policies?tab=assistant' },
  { from: '/deployment', to: '/devices?tab=deployment' },
  { from: '/trust', to: '/devices?tab=trust' }
]

/** Rows whose target is a fixed path. The two that carry a parameter are rendered by a component. */
export const STATIC_REDIRECTS: readonly LegacyRoute[] = LEGACY_ROUTES.filter(
  (route) => !route.from.includes(':') && route.from !== route.to
)

/** Rows that carry a route parameter through, which `<Navigate/>` cannot interpolate on its own. */
export const PARAMETERISED_REDIRECTS: readonly LegacyRoute[] = LEGACY_ROUTES.filter(
  (route) => route.from.includes(':') && route.from !== route.to
)

/**
 * Substitutes the matched parameters into a target.
 *
 * `<Navigate to="/cases/:alertId"/>` does not interpolate in react-router 7 - it navigates to a path
 * containing a literal colon, which matches nothing and lands on the catch-all. So the parameters are
 * substituted here and the result handed to `Navigate` already resolved.
 */
export function resolveTarget(target: string, parameters: Readonly<Record<string, string | undefined>>): string {
  return target.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (whole, name: string) => parameters[name] ?? whole)
}
